import { useState } from "react"
import { Control, useController } from "react-hook-form"
import { TypographyV2 } from "components/v2"
import { CloseIcon } from "components/icons"
import { Modal } from "components/common"
import SelectChipsV2 from "components/v2/components/SelectChipsV2/SelectChipsV2"

interface MultiChipQuestionCardProps {
    question: any
    control: Control<Record<string, any>>
    error?: string
    layoutVariant?: string
    namePrefix?: string
}

interface ChipOption {
    option: string
    comment: string
}

const MultiChipQuestionCard = ({
    question,
    control,
    error,
    layoutVariant,
    namePrefix = "screeningAnswers"
}: MultiChipQuestionCardProps) => {
    const { is_required, question: question_text, definition } = question
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedOption, setSelectedOption] = useState<{ option: string; comment: string }>({
        option: "",
        comment: ""
    })
    const options = (definition as any)?.options?.map((option: string, i: number) =>
        option.trim() == "" ? (i + 1).toString() : option
    )

    const {
        field: { value, onChange }
    } = useController({
        name: `${namePrefix}.${question.id}`,
        control,
        rules: { required: is_required }
    })

    const currentValues = value || []

    const handleModalClose = () => {
        setIsModalOpen(false)
        setSelectedOption({ option: "", comment: "" })
    }

    const handleModalSubmit = () => {
        const newValue = value.map((data: { option: string; comment: string }) => {
            if (data.option === selectedOption.option) {
                return selectedOption
            }
            return data
        })
        console.log(newValue)

        onChange(newValue)
        handleModalClose()
    }

    const handleValuesChange = (newValues: { option: string; comment: "string" }[]) => {
        let isReduced = false
        if (Array.isArray(value)) isReduced = value.length > newValues.length
        onChange(newValues)
        if (newValues.length > 0) setSelectedOption(newValues[newValues.length - 1])
        if (!isReduced) setIsModalOpen(true)
    }

    const handleRemoveOption = (index: number) => {
        const newValue = currentValues.filter((_: any, i: number) => i !== index)
        onChange(newValue)
    }

    return (
        <div className="flex flex-col items-start justify-center gap-[24px] w-full">
            <div className="w-full items-center justify-center text-center">
                <TypographyV2 type="xs">{question?.description}</TypographyV2>
            </div>
            <TypographyV2 type="base">{question_text}</TypographyV2>
            <SelectChipsV2
                disabled={false}
                options={options}
                values={currentValues}
                setValues={handleValuesChange}
                withOther={false}
            />
            {currentValues.length > 0 && (
                <div className="w-full space-y-2">
                    {currentValues.map((item: ChipOption, index: number) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div>
                                <TypographyV2 type="sm">{item.option}</TypographyV2>
                                {item.comment && (
                                    <TypographyV2 type="xs" className="text-gray-600">
                                        {item.comment}
                                    </TypographyV2>
                                )}
                            </div>
                            <button
                                onClick={() => handleRemoveOption(index)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <CloseIcon className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {error && (
                <TypographyV2 type="xs" width="medium" className="text-red-500">
                    {error}
                </TypographyV2>
            )}

            <Modal show={isModalOpen} onClose={handleModalClose} centered cardClassName="w-1/3">
                <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                        <TypographyV2 type="base" width="bold">
                            Add Comment
                        </TypographyV2>
                        <CloseIcon
                            onClick={handleModalClose}
                            className="cursor-pointer text-[#9CA3AF]"
                            height={20}
                            width={20}
                        />
                    </div>
                    <div className="mb-4">
                        <TypographyV2 type="sm" className="mb-2">
                            Selected Option: {selectedOption.option}
                        </TypographyV2>
                        {/* <ControlledTextInputV2
                            placeholder="Enter your comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            multiline
                            rows={4}
                            className="w-full"
                        /> */}
                    </div>
                    <div className="mb-4">
                        <TypographyV2 type="sm" className="mb-2">
                            Comment:
                        </TypographyV2>
                        {/* TODO: Replace this with component */}
                        <textarea
                            className="rounded-lg w-full"
                            rows={3}
                            onChange={(e) =>
                                setSelectedOption((prev) => {
                                    return { ...prev, comment: e.target.value }
                                })
                            }
                        />
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={handleModalSubmit}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                            Add
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default MultiChipQuestionCard
